import { lazy, Suspense, useEffect, useLayoutEffect, type ComponentType } from 'react';
import { match, navigate, useRoute } from './router';
import { store, useBand, useMotion, useStudio, useTextLevel } from './hooks';
import { Shell } from './Shell';
import { Atmosphere } from '@/ui/Atmosphere';
import { ErrorBoundary, recordError } from '@/ui/ErrorBoundary';
import { configureSound, unlockAudio } from '@/ui/sound';
import { toast } from '@/ui/toast';
import { L, setBand, setLang, setTextLevel } from '@/i18n';
import { startAccount } from '@/cloud/account';
import { AccountDialogs } from '@/features/account/Account';
import { SystemLayer } from '@/ui/system';

/**
 * After a new version is deployed, an open tab still points at the old code
 * files, which no longer exist. Reload once to pick up the new version instead
 * of showing an error (at most once per minute, so a real outage can't loop).
 */
function reloadForNewVersion(e: unknown): Promise<never> {
  const stale = /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(String((e as Error)?.message ?? e));
  try {
    const last = Number(sessionStorage.getItem('studio.versionReload') ?? 0);
    if (stale && Date.now() - last > 60_000) {
      sessionStorage.setItem('studio.versionReload', String(Date.now()));
      location.reload();
      return new Promise<never>(() => undefined);
    }
  } catch {
    /* storage unavailable: fall through to the error screen */
  }
  return Promise.reject(e);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const page = <T extends Record<string, any>>(loader: () => Promise<T>, name: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[name] as ComponentType<any> }), reloadForNewVersion));

const Welcome = page(() => import('@/features/onboarding/Welcome'), 'Welcome');
const Today = page(() => import('@/features/today/Today'), 'Today');
const LearnHub = page(() => import('@/features/learn/LearnHub'), 'LearnHub');
const LearnSession = page(() => import('@/features/learn/LearnSession'), 'LearnSession');
const Assessment = page(() => import('@/features/assessment/Assessment'), 'Assessment');
const Courses = page(() => import('@/features/courses/Courses'), 'Courses');
const CourseDetail = page(() => import('@/features/courses/Courses'), 'CourseDetail');
const Subjects = page(() => import('@/features/subjects/Subjects'), 'Subjects');
const SubjectDetail = page(() => import('@/features/subjects/Subjects'), 'SubjectDetail');
const SkillDetail = page(() => import('@/features/subjects/SkillDetail'), 'SkillDetail');
const KnowledgeMap = page(() => import('@/features/map/KnowledgeMap'), 'KnowledgeMap');
const Planner = page(() => import('@/features/planner/Planner'), 'Planner');
const Exams = page(() => import('@/features/exams/Exams'), 'Exams');
const ExamSession = page(() => import('@/features/exams/ExamSession'), 'ExamSession');
const Goals = page(() => import('@/features/planner/Goals'), 'Goals');
const Mastery = page(() => import('@/features/progress/Mastery'), 'Mastery');
const Analytics = page(() => import('@/features/progress/Analytics'), 'Analytics');
const Mistakes = page(() => import('@/features/progress/Mistakes'), 'Mistakes');
const Reviews = page(() => import('@/features/progress/Reviews'), 'Reviews');
const Profile = page(() => import('@/features/profile/LearningProfile'), 'LearningProfile');
const Calibration = page(() => import('@/features/calibration/Calibration'), 'Calibration');
const Quests = page(() => import('@/features/system/Quests'), 'Quests');
const Achievements = page(() => import('@/features/system/Achievements'), 'Achievements');
const Notes = page(() => import('@/features/system/Notes'), 'Notes');
const Palace = page(() => import('@/features/palace/Palace'), 'Palace');
const Settings = page(() => import('@/features/system/Settings'), 'Settings');
const Research = page(() => import('@/features/system/Research'), 'Research');
const Feynman = page(() => import('@/features/tools/Feynman'), 'Feynman');
const Writing = page(() => import('@/features/tools/Writing'), 'Writing');
const Boss = page(() => import('@/features/boss/Boss'), 'Boss');
const Account = page(() => import('@/features/account/Account'), 'Account');
const Chat = page(() => import('@/features/chat/Chat'), 'Chat');

const ROUTES: [string, ComponentType<any>][] = [
  ['/today', Today],
  ['/learn', LearnHub],
  ['/learn/:skillId', LearnSession],
  ['/assess', Assessment],
  ['/courses', Courses],
  ['/courses/:courseId', CourseDetail],
  ['/subjects', Subjects],
  ['/subjects/:subjectId', SubjectDetail],
  ['/skill/:skillId', SkillDetail],
  ['/map', KnowledgeMap],
  ['/planner', Planner],
  ['/exams', Exams],
  ['/exams/session', ExamSession],
  ['/goals', Goals],
  ['/mastery', Mastery],
  ['/analytics', Analytics],
  ['/mistakes', Mistakes],
  ['/reviews', Reviews],
  ['/profile', Profile],
  ['/calibration', Calibration],
  ['/quests', Quests],
  ['/achievements', Achievements],
  ['/notes', Notes],
  ['/palace', Palace],
  ['/settings', Settings],
  ['/research', Research],
  ['/feynman', Feynman],
  ['/feynman/:skillId', Feynman],
  ['/writing', Writing],
  ['/boss/:bossId', Boss],
  ['/account', Account],
  ['/chat', Chat],
];

function Loading() {
  return (
    <div className="page" aria-busy="true">
      <div className="loading-scan"><span className="sys"><span className="code">SYS</span>{L('Loading…', 'Laster …')}</span></div>
    </div>
  );
}

/** Global safety net: errors outside React (timers, promises) are recorded and shown, never silent. */
function useGlobalErrorCapture() {
  useEffect(() => {
    let last = 0;
    const report = (where: string, err: unknown) => {
      recordError(where, err);
      if (Date.now() - last > 4000) {
        last = Date.now();
        toast(L('Something went wrong', 'Noe gikk galt'), L('Studio recovered. Your progress is saved.', 'Studio fortsatte. Fremgangen din er lagret.'), 'error');
      }
    };
    const onError = (e: ErrorEvent) => report('window', e.error ?? e.message);
    const onRejection = (e: PromiseRejectionEvent) => report('promise', e.reason);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
}

export function App() {
  const route = useRoute();
  const hasProfile = useStudio((s) => !!s.profile);
  const settings = useStudio((s) => s.settings);
  const band = useBand();
  const textLevel = useTextLevel();
  const motion = useMotion();
  const appearance = settings.appearance;

  // Module-level language/band must be current before any child renders.
  setLang(settings.language);
  setBand(band);
  setTextLevel(textLevel);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.motion = motion;
    root.dataset.effects = settings.effects;
    root.dataset.theme = settings.theme ?? 'cyan';
    root.dataset.band = band;
    root.dataset.text = textLevel;
    root.dataset.appearance = appearance;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', appearance === 'day' ? '#eef4ff' : '#05070f');
  }, [motion, settings.effects, settings.theme, band, textLevel, appearance]);

  useEffect(() => configureSound(settings.sound, settings.volume), [settings.sound, settings.volume]);

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    store.dispatch({ type: 'TICK' });
    const id = setInterval(() => store.dispatch({ type: 'TICK' }), 5 * 60_000);
    return () => clearInterval(id);
  }, []);

  useGlobalErrorCapture();

  // Optional account: checks the session and syncs progress in the background.
  useEffect(() => { void startAccount(store); }, []);

  const uiKey = `${settings.language}:${band}:${textLevel}`;

  if (!hasProfile) {
    return (
      <>
        <Atmosphere />
        <ErrorBoundary where="welcome">
          <Suspense fallback={<Loading />}><Welcome key={uiKey} /></Suspense>
        </ErrorBoundary>
        <AccountDialogs />
        <SystemLayer />
      </>
    );
  }

  let Component: ComponentType<any> | null = null;
  let params: Record<string, string> = {};
  for (const [pattern, C] of ROUTES) {
    const m = match(pattern, route.path);
    if (m) {
      Component = C;
      params = m;
      break;
    }
  }
  if (!Component) {
    navigate('/today', true);
    return null;
  }
  return (
    <>
      <Atmosphere />
      <Shell key={uiKey}>
        <ErrorBoundary key={route.path} where={route.path}>
          <Suspense fallback={<Loading />}>
            <Component key={route.path} {...params} query={route.query} />
          </Suspense>
        </ErrorBoundary>
      </Shell>
      <AccountDialogs />
      <SystemLayer />
    </>
  );
}
